import React, { useState, useEffect, useMemo } from "react";
import Content from "../../components/Content";
import Layout from "../../containers/LayoutAdministrativo";
import SolicitudesService from "../../services/SolicitudesService";
import SucursalesService from "../../services/SucursalesService";
import PacientesService from "../../services/PacientesService";

import logoLaboratorio from "../../img/logo-laboratorio.jpg";
import Icon from "@mdi/react";
import { mdiFilePdfBox } from "@mdi/js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { ejecutarConCargandoPDF } from "../../utils/pdfLoadingUtils";

import {
  Paper,
  Typography,
  Stack,
  Box,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  IconButton,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import Swal from "sweetalert2";

const EstadisticasPacientes = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [sucursales, setSucursales] = useState([]);
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hoveredBar, setHoveredBar] = useState(null);

  useEffect(() => {
    document.title = "Estadísticas de Pacientes";
    fetchDatosIniciales();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDatosIniciales = async () => {
    setLoading(true);
    try {
      const [resSol, resSuc, resPac] = await Promise.all([
        SolicitudesService.getSolicitudes().catch(() => ({ data: [] })),
        SucursalesService.getSucursales().catch(() => ({ data: [] })),
        PacientesService.getPacientes().catch(() => ({ data: [] })),
      ]);

      const listaSol = Array.isArray(resSol?.data?.solicitudes)
        ? resSol.data.solicitudes
        : Array.isArray(resSol?.data)
        ? resSol.data
        : [];

      const listaSuc = Array.isArray(resSuc?.data?.sucursales)
        ? resSuc.data.sucursales
        : Array.isArray(resSuc?.data)
        ? resSuc.data
        : [];

      const listaPac = Array.isArray(resPac?.data?.pacientes)
        ? resPac.data.pacientes
        : Array.isArray(resPac?.data)
        ? resPac.data
        : [];

      setSolicitudes(listaSol);
      setSucursales(listaSuc);
      setPacientes(listaPac);
    } catch (error) {
      console.error("Error al cargar datos iniciales:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudieron obtener los datos para el reporte.",
        icon: "error",
        confirmButtonColor: "#006787",
      });
    } finally {
      setLoading(false);
    }
  };

  // Normalizador de sexo / género
  const getSexoNormalizado = (val) => {
    if (!val) return "Femenino";
    const s = String(val).trim().toUpperCase();
    if (s.startsWith("M") || s.includes("MASCULINO") || s.includes("HOMBRE")) {
      return "Masculino";
    }
    return "Femenino";
  };

  // Mapa de Pacientes por ID
  const pacientesMap = useMemo(() => {
    const map = {};
    pacientes.forEach((p) => {
      const pId = p.idPaciente || p.id_paciente || p.id;
      if (pId) {
        map[pId] = p;
      }
    });
    return map;
  }, [pacientes]);

  // --- DATOS TABLA 1: Visitas por Sucursal y Sexo ---
  const datosTabla1 = useMemo(() => {
    return sucursales.map((suc) => {
      const sucId = suc.idSucursal || suc.id;
      const sucNombre = suc.nombre || suc.nombreSucursal || `Sucursal ${sucId}`;

      const solsSuc = solicitudes.filter(
        (sol) => String(sol.idSucursal || sol.id_sucursal) === String(sucId)
      );

      let femeninoCount = 0;
      let masculinoCount = 0;

      solsSuc.forEach((sol) => {
        const pId = sol.idPaciente || sol.id_paciente;
        const pacObj = pacientesMap[pId] || {};
        const sexoRaw = pacObj.sexo || pacObj.genero || sol.sexo || sol.genero;
        const sexoNorm = getSexoNormalizado(sexoRaw);

        if (sexoNorm === "Masculino") {
          masculinoCount++;
        } else {
          femeninoCount++;
        }
      });

      const totalVisitas = femeninoCount + masculinoCount;
      const porcFemenino =
        totalVisitas > 0 ? ((femeninoCount / totalVisitas) * 100).toFixed(2) : "0.00";
      const porcMasculino =
        totalVisitas > 0 ? ((masculinoCount / totalVisitas) * 100).toFixed(2) : "0.00";

      return {
        idSucursal: sucId,
        sucursal: sucNombre,
        femeninoVisitas: femeninoCount,
        femeninoPorcentaje: porcFemenino,
        masculinoVisitas: masculinoCount,
        masculinoPorcentaje: porcMasculino,
        totalVisitas: totalVisitas,
      };
    });
  }, [sucursales, solicitudes, pacientesMap]);

  // Gran Total Tabla 1
  const totalesTabla1 = useMemo(() => {
    let totFem = 0;
    let totMas = 0;
    datosTabla1.forEach((row) => {
      totFem += row.femeninoVisitas;
      totMas += row.masculinoVisitas;
    });
    const granTotal = totFem + totMas;
    const porcFem = granTotal > 0 ? ((totFem / granTotal) * 100).toFixed(2) : "0.00";
    const porcMas = granTotal > 0 ? ((totMas / granTotal) * 100).toFixed(2) : "0.00";

    return {
      femeninoVisitas: totFem,
      femeninoPorcentaje: porcFem,
      masculinoVisitas: totMas,
      masculinoPorcentaje: porcMas,
      totalVisitas: granTotal,
    };
  }, [datosTabla1]);

  // --- DATOS TABLA 2: Recurrencia de Pacientes ---
  const datosTabla2 = useMemo(() => {
    return sucursales.map((suc) => {
      const sucId = suc.idSucursal || suc.id;
      const sucNombre = suc.nombre || suc.nombreSucursal || `Sucursal ${sucId}`;

      const solsSuc = solicitudes.filter(
        (sol) => String(sol.idSucursal || sol.id_sucursal) === String(sucId)
      );

      // Conteo de solicitudes por idPaciente en esta sucursal
      const pacienteCounts = {};
      solsSuc.forEach((sol) => {
        const pId = sol.idPaciente || sol.id_paciente;
        if (pId) {
          pacienteCounts[pId] = (pacienteCounts[pId] || 0) + 1;
        }
      });

      let pacUnica = 0;
      let pacRecurrentes = 0;
      let solRecurrentesTotales = 0;

      Object.values(pacienteCounts).forEach((count) => {
        if (count === 1) {
          pacUnica++;
        } else if (count > 1) {
          pacRecurrentes++;
          solRecurrentesTotales += count;
        }
      });

      const pacAtendidos = pacUnica + pacRecurrentes;
      const porcUnica =
        pacAtendidos > 0 ? ((pacUnica / pacAtendidos) * 100).toFixed(2) : "0.00";
      const porcRecurrentes =
        pacAtendidos > 0 ? ((pacRecurrentes / pacAtendidos) * 100).toFixed(2) : "0.00";

      const promedioOrdenesRecurrentes =
        pacRecurrentes > 0 ? (solRecurrentesTotales / pacRecurrentes).toFixed(2) : "0.00";

      return {
        idSucursal: sucId,
        sucursal: sucNombre,
        unicaVisita: pacUnica,
        recurrentes: pacRecurrentes,
        atendidos: pacAtendidos,
        porcUnica: porcUnica,
        porcRecurrentes: porcRecurrentes,
        solRecurrentesTotales: solRecurrentesTotales,
        promedioOrdenesRecurrentes: promedioOrdenesRecurrentes,
      };
    });
  }, [sucursales, solicitudes]);

  // Gran Total Tabla 2
  const totalesTabla2 = useMemo(() => {
    let totUnica = 0;
    let totRec = 0;
    let totSolRec = 0;

    datosTabla2.forEach((row) => {
      totUnica += row.unicaVisita;
      totRec += row.recurrentes;
      totSolRec += row.solRecurrentesTotales;
    });

    const totAtendidos = totUnica + totRec;
    const porcUnica =
      totAtendidos > 0 ? ((totUnica / totAtendidos) * 100).toFixed(2) : "0.00";
    const porcRec =
      totAtendidos > 0 ? ((totRec / totAtendidos) * 100).toFixed(2) : "0.00";
    const promedio =
      totRec > 0 ? (totSolRec / totRec).toFixed(2) : "0.00";

    return {
      unicaVisita: totUnica,
      recurrentes: totRec,
      atendidos: totAtendidos,
      porcUnica: porcUnica,
      porcRecurrentes: porcRec,
      promedioOrdenesRecurrentes: promedio,
    };
  }, [datosTabla2]);

  // --- CÁLCULO DINÁMICO DE LA ESCALA EN EJE Y (GRÁFICA) ---
  // Si max < 10 -> de 10 en 10 (o escala proporcional 0 a 10)
  // Si max <= 100 -> de 10 en 10
  // Si max <= 1000 -> de 100 en 100
  // Si max > 1000 -> de 1000 en 1000, etc.
  const configuracionGraficaY = useMemo(() => {
    let max = 0;
    datosTabla1.forEach((d) => {
      if (d.femeninoVisitas > max) max = d.femeninoVisitas;
      if (d.masculinoVisitas > max) max = d.masculinoVisitas;
    });

    let step = 10;
    let roundedMax = 10;

    if (max <= 10) {
      step = max <= 5 ? 1 : 2;
      roundedMax = max <= 5 ? 5 : 10;
    } else if (max <= 100) {
      step = 10;
      roundedMax = Math.ceil(max / 10) * 10;
    } else if (max <= 1000) {
      step = 100;
      roundedMax = Math.ceil(max / 100) * 100;
    } else {
      // Mayor a 1000: de 1000 en 1000 (o según la magnitud)
      step = 1000;
      if (max > 10000) {
        const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
        step = magnitude / 2;
      }
      roundedMax = Math.ceil(max / step) * step;
    }

    const ticks = [];
    for (let v = roundedMax; v >= 0; v -= step) {
      ticks.push(v);
    }

    return { max, step, roundedMax, ticks };
  }, [datosTabla1]);

  // --- EXPORTAR A PDF ---
  const handleExportarPDF = async () => {
    if (datosTabla1.length === 0) {
      Swal.fire({
        title: "Sin Información",
        text: "No existen registros para exportar.",
        icon: "info",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#006787",
      });
      return;
    }

    await ejecutarConCargandoPDF(
      async () => {
        const doc = new jsPDF("portrait", "mm", "a4");
        const startY = 12;

    let sucursalActualObj = sucursales[0] || {};
    const sucursalNomText = (sucursalActualObj.nombre || "LABORATORIO DE ANÁLISIS CLÍNICOS").toUpperCase();
    const calleText = [sucursalActualObj.calle, sucursalActualObj.numExterior, sucursalActualObj.numInterior].filter(Boolean).join(" ");
    const colCpText = `Col. ${sucursalActualObj.colonia || ""} C.P. ${sucursalActualObj.cPostal || ""} ${sucursalActualObj.ciudad || ""}, ${sucursalActualObj.estado || ""}`;

    // Dibujar Logo
    try {
      doc.addImage(logoLaboratorio, "JPEG", 14, startY, 45, 24);
    } catch (e) {}

    // Recuadro "INFORMES"
    doc.setFillColor(24, 35, 79);
    doc.rect(115, startY, 81, 7, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("INFORMES", 155.5, startY + 5, { align: "center" });

    // Encabezado de la Sucursal en la derecha
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(8);
    doc.setFont("helvetica", "bold");
    doc.text(sucursalNomText, 196, startY + 12, { align: "right" });

    let currY = startY + 16;
    if (calleText.trim()) {
      doc.setFont("helvetica", "normal");
      doc.text(`Dirección: ${calleText}`, 196, currY, { align: "right" });
      currY += 4;
    }
    doc.setFont("helvetica", "normal");
    doc.text(colCpText, 196, currY, { align: "right" });

    // Título principal
    currY += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(24, 35, 79);
    doc.text("ESTADÍSTICAS DE PACIENTES", 14, currY);

    let nextY = currY + 10;

    // --- TABLA 1 EN PDF: Visitas por Sucursal y Sexo ---
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 103, 135);
    doc.text("Visitas por Sucursal y Sexo", 14, nextY);
    nextY += 4;

    const bodyTabla1PDF = [];
    datosTabla1.forEach((row) => {
      bodyTabla1PDF.push([
        row.sucursal,
        "Femenino",
        row.femeninoVisitas.toLocaleString(),
        `${row.femeninoPorcentaje}%`,
      ]);
      bodyTabla1PDF.push([
        row.sucursal,
        "Masculino",
        row.masculinoVisitas.toLocaleString(),
        `${row.masculinoPorcentaje}%`,
      ]);
    });

    const totalFemPorc = totalesTabla1.totalVisitas > 0 ? ((totalesTabla1.femeninoVisitas / totalesTabla1.totalVisitas) * 100).toFixed(2) : "0.00";
    const totalMasPorc = totalesTabla1.totalVisitas > 0 ? ((totalesTabla1.masculinoVisitas / totalesTabla1.totalVisitas) * 100).toFixed(2) : "0.00";

    bodyTabla1PDF.push([
      "TOTAL ACUMULADO",
      "Femenino",
      totalesTabla1.femeninoVisitas.toLocaleString(),
      `${totalFemPorc}%`,
    ]);
    bodyTabla1PDF.push([
      "TOTAL ACUMULADO",
      "Masculino",
      totalesTabla1.masculinoVisitas.toLocaleString(),
      `${totalMasPorc}%`,
    ]);

    autoTable(doc, {
      startY: nextY,
      head: [
        [
          { content: "Sucursal", styles: { halign: "left" } },
          { content: "Sexo", styles: { halign: "center" } },
          { content: "Total visitas", styles: { halign: "center" } },
          { content: "Porcentaje", styles: { halign: "center" } },
        ],
      ],
      body: bodyTabla1PDF,
      headStyles: { fillColor: [0, 103, 135], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 8 },
      styles: { fontSize: 8, cellPadding: 2, halign: "center" },
      columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
    });

    nextY = doc.lastAutoTable.finalY + 10;

    // --- TABLA 2 EN PDF: Recurrencia de Pacientes ---
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0, 103, 135);
    doc.text("Recurrencia de Pacientes por Sucursal", 14, nextY);
    nextY += 4;

    const bodyTabla2PDF = datosTabla2.map((row) => [
      row.sucursal,
      row.unicaVisita.toLocaleString(),
      row.recurrentes.toLocaleString(),
      row.atendidos.toLocaleString(),
      `${row.porcUnica}%`,
      `${row.porcRecurrentes}%`,
      row.promedioOrdenesRecurrentes,
    ]);

    bodyTabla2PDF.push([
      "TOTALES / PROMEDIO GENERAL",
      totalesTabla2.unicaVisita.toLocaleString(),
      totalesTabla2.recurrentes.toLocaleString(),
      totalesTabla2.atendidos.toLocaleString(),
      `${totalesTabla2.porcUnica}%`,
      `${totalesTabla2.porcRecurrentes}%`,
      totalesTabla2.promedioOrdenesRecurrentes,
    ]);

    autoTable(doc, {
      startY: nextY,
      head: [
        [
          { content: "Sucursal", rowSpan: 2, styles: { halign: "left", valign: "middle" } },
          { content: "Totales", colSpan: 3, styles: { halign: "center", valign: "middle" } },
          { content: "Porcentaje", colSpan: 2, styles: { halign: "center", valign: "middle" } },
          { content: "Promedio", colSpan: 1, styles: { halign: "center", valign: "middle" } },
        ],
        [
          { content: "Pacientes única visita", styles: { halign: "center" } },
          { content: "Pacientes recurrentes", styles: { halign: "center" } },
          { content: "Pacientes atendidos", styles: { halign: "center" } },
          { content: "Pacientes única visita", styles: { halign: "center" } },
          { content: "Pacientes recurrentes", styles: { halign: "center" } },
          { content: "Órdenes realizadas a pacientes recurrentes", styles: { halign: "center" } },
        ],
      ],
      body: bodyTabla2PDF,
      headStyles: { fillColor: [24, 35, 79], textColor: [255, 255, 255], fontStyle: "bold", fontSize: 7.5 },
      styles: { fontSize: 7.5, cellPadding: 2, halign: "center" },
      columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
    });

    // Pie de Página
    const pageCount = doc.internal.getNumberOfPages();
    const hoyImpStr = new Date().toLocaleString();

    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(120, 120, 120);
      doc.text(`Impreso: ${hoyImpStr}`, 14, 287);
      doc.text(`Página ${i} de ${pageCount}`, 196, 287, { align: "right" });
    }

        doc.save(`Estadisticas_Pacientes.pdf`);
      },
      "Generando reporte de Estadísticas de Pacientes en PDF...",
      "Procesando documento"
    );
  };

  return (
    <Layout>
      <Content>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "100%",
            minHeight: "80vh",
            py: 3,
          }}
        >
          <Stack spacing={3} sx={{ width: "100%", pl: "3vw", pr: "10vw", boxSizing: "border-box" }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 4,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
              className="paperSol"
            >
              {/* Título Principal al estilo de los demás informes */}
              <Box sx={{ width: "100%", display: "flex", justifyContent: "center", mb: 3 }}>
                <Typography
                  variant="h6"
                  sx={{
                    background: "linear-gradient(90deg, #006787, #014e65)",
                    color: "white",
                    padding: "0.5vw 2vw",
                    borderRadius: "8px",
                    textAlign: "center",
                    fontWeight: "bold",
                  }}
                >
                  ESTADÍSTICAS DE PACIENTES
                </Typography>
              </Box>

              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                  <CircularProgress size={50} sx={{ color: "#006787" }} />
                </Box>
              ) : (
                <Stack spacing={4} sx={{ width: "100%" }}>
                  {/* --- SECCIÓN DE LA GRÁFICA --- */}
                  <Paper elevation={2} sx={{ p: 3, borderRadius: 3, backgroundColor: "#ffffff" }}>
                    {/* Título de la gráfica */}
                    <Typography
                      variant="h6"
                      align="center"
                      fontWeight="bold"
                      sx={{ color: "#1a202c", mb: 2, fontFamily: "Roboto-Medium" }}
                    >
                      Estadísticas de Recurrencia de Pacientes
                    </Typography>

                    {/* Badges de Género (Rosa = Femenino, Azul = Masculino) */}
                    <Stack direction="row" spacing={3} justifyContent="center" sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          backgroundColor: "#fce4ec",
                          border: "2px solid #ec407a",
                          borderRadius: "8px",
                          px: 2.5,
                          py: 0.8,
                          boxShadow: "0 2px 4px rgba(236, 64, 122, 0.15)",
                        }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            backgroundColor: "#ec407a",
                          }}
                        />
                        <Typography fontWeight="bold" sx={{ color: "#880e4f", fontSize: "0.95rem" }}>
                          Femenino
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          backgroundColor: "#e3f2fd",
                          border: "2px solid #1976d2",
                          borderRadius: "8px",
                          px: 2.5,
                          py: 0.8,
                          boxShadow: "0 2px 4px rgba(25, 118, 210, 0.15)",
                        }}
                      >
                        <Box
                          sx={{
                            width: 16,
                            height: 16,
                            borderRadius: "4px",
                            backgroundColor: "#1976d2",
                          }}
                        />
                        <Typography fontWeight="bold" sx={{ color: "#0d47a1", fontSize: "0.95rem" }}>
                          Masculino
                        </Typography>
                      </Box>
                    </Stack>

                    {/* Renderizado de la Gráfica de Barras SVG con escala dinámica en Eje Y */}
                    <Box sx={{ width: "100%", overflowX: "auto", py: 1 }}>
                      <Box sx={{ minWidth: 600, height: 380, position: "relative" }}>
                        <svg width="100%" height="100%" viewBox="0 0 800 350" preserveAspectRatio="none">
                          {/* Eje Y: Líneas de cuadrícula e indicadores dinámicos */}
                          {configuracionGraficaY.ticks.map((tickVal) => {
                            const yPos = 300 - (tickVal / configuracionGraficaY.roundedMax) * 260;
                            return (
                              <g key={tickVal}>
                                <line
                                  x1="65"
                                  y1={yPos}
                                  x2="780"
                                  y2={yPos}
                                  stroke="#e2e8f0"
                                  strokeDasharray={tickVal === 0 ? "none" : "3 3"}
                                  strokeWidth={tickVal === 0 ? "2" : "1"}
                                />
                                <text
                                  x="58"
                                  y={yPos + 4}
                                  fill="#64748b"
                                  fontSize="11"
                                  fontWeight="bold"
                                  textAnchor="end"
                                >
                                  {tickVal.toLocaleString()}
                                </text>
                              </g>
                            );
                          })}

                          {/* Eje vertical Y principal */}
                          <line x1="65" y1="30" x2="65" y2="300" stroke="#94a3b8" strokeWidth="2" />
                          <text
                            x="20"
                            y="160"
                            fill="#475569"
                            fontSize="11"
                            fontWeight="bold"
                            transform="rotate(-90 20 160)"
                            textAnchor="middle"
                          >
                            Visitas de los Pacientes
                          </text>

                          {/* Eje horizontal X */}
                          <line x1="65" y1="300" x2="780" y2="300" stroke="#94a3b8" strokeWidth="2" />

                          {/* Render de Barras agrupadas por Sucursal */}
                          {datosTabla1.map((d, index) => {
                            const totalGrupos = datosTabla1.length;
                            const availableWidth = 715;
                            const groupWidth = availableWidth / Math.max(totalGrupos, 1);
                            const groupX = 65 + index * groupWidth;
                            const barWidth = Math.min(32, (groupWidth - 20) / 2);

                            const hFem = (d.femeninoVisitas / configuracionGraficaY.roundedMax) * 260;
                            const yFem = 300 - hFem;

                            const hMas = (d.masculinoVisitas / configuracionGraficaY.roundedMax) * 260;
                            const yMas = 300 - hMas;

                            const xFem = groupX + groupWidth / 2 - barWidth - 3;
                            const xMas = groupX + groupWidth / 2 + 3;

                            return (
                              <g key={d.idSucursal}>
                                {/* Barra Femenino (Rosa) */}
                                <rect
                                  x={xFem}
                                  y={yFem}
                                  width={barWidth}
                                  height={hFem}
                                  fill="#ec407a"
                                  rx="4"
                                  ry="4"
                                  style={{ transition: "all 0.3s ease", cursor: "pointer" }}
                                  onMouseEnter={() =>
                                    setHoveredBar({
                                      sucursal: d.sucursal,
                                      sexo: "Femenino",
                                      visitas: d.femeninoVisitas,
                                    })
                                  }
                                  onMouseLeave={() => setHoveredBar(null)}
                                />
                                {d.femeninoVisitas > 0 && (
                                  <text
                                    x={xFem + barWidth / 2}
                                    y={yFem - 6}
                                    fill="#ec407a"
                                    fontSize="10"
                                    fontWeight="bold"
                                    textAnchor="middle"
                                  >
                                    {d.femeninoVisitas}
                                  </text>
                                )}

                                {/* Barra Masculino (Azul) */}
                                <rect
                                  x={xMas}
                                  y={yMas}
                                  width={barWidth}
                                  height={hMas}
                                  fill="#1976d2"
                                  rx="4"
                                  ry="4"
                                  style={{ transition: "all 0.3s ease", cursor: "pointer" }}
                                  onMouseEnter={() =>
                                    setHoveredBar({
                                      sucursal: d.sucursal,
                                      sexo: "Masculino",
                                      visitas: d.masculinoVisitas,
                                    })
                                  }
                                  onMouseLeave={() => setHoveredBar(null)}
                                />
                                {d.masculinoVisitas > 0 && (
                                  <text
                                    x={xMas + barWidth / 2}
                                    y={yMas - 6}
                                    fill="#1976d2"
                                    fontSize="10"
                                    fontWeight="bold"
                                    textAnchor="middle"
                                  >
                                    {d.masculinoVisitas}
                                  </text>
                                )}

                                {/* Nombre de la Sucursal en Eje X */}
                                <text
                                  x={groupX + groupWidth / 2}
                                  y="322"
                                  fill="#334155"
                                  fontSize="11"
                                  fontWeight="bold"
                                  textAnchor="middle"
                                >
                                  {d.sucursal.length > 18
                                    ? `${d.sucursal.substring(0, 16)}...`
                                    : d.sucursal}
                                </text>
                              </g>
                            );
                          })}
                        </svg>

                        {/* Tooltip flotante al pasar el mouse por una barra */}
                        {hoveredBar && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 10,
                              right: 20,
                              backgroundColor: "rgba(15, 23, 42, 0.85)",
                              color: "white",
                              p: 1.2,
                              borderRadius: 2,
                              fontSize: "0.85rem",
                              pointerEvents: "none",
                              boxShadow: 3,
                            }}
                          >
                            <Typography variant="body2" fontWeight="bold">
                              {hoveredBar.sucursal}
                            </Typography>
                            <Typography variant="caption" sx={{ color: hoveredBar.sexo === "Femenino" ? "#f48fb1" : "#90caf9" }}>
                              {hoveredBar.sexo}: <strong>{hoveredBar.visitas} visitas</strong>
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Paper>

                  {/* --- TABLA 1: Visitas por Sucursal y Sexo --- */}
                  <Paper elevation={2} sx={{ p: 3, borderRadius: 3, backgroundColor: "#ffffff" }}>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: "#006787", mb: 2, fontFamily: "Roboto-Medium" }}
                    >
                      Visitas por Sucursal y Sexo
                    </Typography>

                    <Box sx={{ overflowX: "auto" }}>
                      <Table size="small" sx={{ border: "1px solid #e2e8f0" }}>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: "#006787" }}>
                            <TableCell sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                              Sucursal
                            </TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                              Sexo
                            </TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                              Total visitas
                            </TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}>
                              Porcentaje
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {datosTabla1.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={4} align="center" sx={{ py: 3, color: "#64748b" }}>
                                No hay registros de visitas.
                              </TableCell>
                            </TableRow>
                          ) : (
                            datosTabla1.map((row) => (
                              <React.Fragment key={row.idSucursal}>
                                {/* Femenino */}
                                <TableRow sx={{ "&:hover": { backgroundColor: "#fdf2f8" } }}>
                                  <TableCell rowSpan={2} sx={{ fontWeight: "bold", color: "#1e293b", borderBottom: "2px solid #cbd5e1" }}>
                                    {row.sucursal}
                                  </TableCell>
                                  <TableCell align="center" sx={{ color: "#c2185b", fontWeight: "bold" }}>
                                    Femenino
                                  </TableCell>
                                  <TableCell align="center">{row.femeninoVisitas.toLocaleString()}</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#ec407a" }}>
                                    {row.femeninoPorcentaje}%
                                  </TableCell>
                                </TableRow>
                                {/* Masculino */}
                                <TableRow sx={{ "&:hover": { backgroundColor: "#eff6ff" }, borderBottom: "2px solid #cbd5e1" }}>
                                  <TableCell align="center" sx={{ color: "#1565c0", fontWeight: "bold" }}>
                                    Masculino
                                  </TableCell>
                                  <TableCell align="center">{row.masculinoVisitas.toLocaleString()}</TableCell>
                                  <TableCell align="center" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                                    {row.masculinoPorcentaje}%
                                  </TableCell>
                                </TableRow>
                              </React.Fragment>
                            ))
                          )}
                          {/* Fila Total General */}
                          {datosTabla1.length > 0 && (
                            <TableRow sx={{ backgroundColor: "#f8fafc", borderTop: "2px solid #006787" }}>
                              <TableCell rowSpan={2} sx={{ fontWeight: "bold", color: "#006787", fontSize: "0.95rem" }}>
                                TOTAL ACUMULADO
                              </TableCell>
                              <TableCell align="center" sx={{ color: "#c2185b", fontWeight: "bold" }}>
                                Femenino
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                {totalesTabla1.femeninoVisitas.toLocaleString()}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#ec407a" }}>
                                {totalesTabla1.femeninoPorcentaje}%
                              </TableCell>
                            </TableRow>
                          )}
                          {datosTabla1.length > 0 && (
                            <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                              <TableCell align="center" sx={{ color: "#1565c0", fontWeight: "bold" }}>
                                Masculino
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                {totalesTabla1.masculinoVisitas.toLocaleString()}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#1976d2" }}>
                                {totalesTabla1.masculinoPorcentaje}%
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Paper>

                  {/* --- TABLA 2: Recurrencia de Pacientes --- */}
                  <Paper elevation={2} sx={{ p: 3, borderRadius: 3, backgroundColor: "#ffffff" }}>
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{ color: "#18234f", mb: 2, fontFamily: "Roboto-Medium" }}
                    >
                      Recurrencia de Pacientes por Sucursal
                    </Typography>

                    <Box sx={{ overflowX: "auto" }}>
                      <Table size="small" sx={{ border: "1px solid #e2e8f0" }}>
                        <TableHead>
                          {/* Fila de Encabezados Principales Dobles */}
                          <TableRow sx={{ backgroundColor: "#18234f" }}>
                            <TableCell
                              rowSpan={2}
                              sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem", borderRight: "1px solid rgba(255,255,255,0.2)" }}
                            >
                              Sucursal
                            </TableCell>
                            <TableCell
                              colSpan={3}
                              align="center"
                              sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem", borderRight: "1px solid rgba(255,255,255,0.2)" }}
                            >
                              Totales
                            </TableCell>
                            <TableCell
                              colSpan={2}
                              align="center"
                              sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem", borderRight: "1px solid rgba(255,255,255,0.2)" }}
                            >
                              Porcentaje
                            </TableCell>
                            <TableCell
                              colSpan={1}
                              align="center"
                              sx={{ color: "white", fontWeight: "bold", fontSize: "0.9rem" }}
                            >
                              Promedio
                            </TableCell>
                          </TableRow>

                          {/* Subencabezados */}
                          <TableRow sx={{ backgroundColor: "#27387a" }}>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.82rem" }}>
                              Pacientes única visita
                            </TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.82rem" }}>
                              Pacientes recurrentes
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ color: "white", fontWeight: "bold", fontSize: "0.82rem", borderRight: "1px solid rgba(255,255,255,0.2)" }}
                            >
                              Pacientes atendidos
                            </TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.82rem" }}>
                              Pacientes única visita
                            </TableCell>
                            <TableCell
                              align="center"
                              sx={{ color: "white", fontWeight: "bold", fontSize: "0.82rem", borderRight: "1px solid rgba(255,255,255,0.2)" }}
                            >
                              Pacientes recurrentes
                            </TableCell>
                            <TableCell align="center" sx={{ color: "white", fontWeight: "bold", fontSize: "0.82rem" }}>
                              Órdenes realizadas a pacientes recurrentes
                            </TableCell>
                          </TableRow>
                        </TableHead>

                        <TableBody>
                          {datosTabla2.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={7} align="center" sx={{ py: 3, color: "#64748b" }}>
                                No existen datos de recurrencia.
                              </TableCell>
                            </TableRow>
                          ) : (
                            datosTabla2.map((row) => (
                              <TableRow key={row.idSucursal} sx={{ "&:hover": { backgroundColor: "#f1f5f9" } }}>
                                <TableCell sx={{ fontWeight: "bold", color: "#1e293b" }}>{row.sucursal}</TableCell>
                                <TableCell align="center">{row.unicaVisita.toLocaleString()}</TableCell>
                                <TableCell align="center">{row.recurrentes.toLocaleString()}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", color: "#0f172a", backgroundColor: "#f8fafc" }}>
                                  {row.atendidos.toLocaleString()}
                                </TableCell>
                                <TableCell align="center">{row.porcUnica}%</TableCell>
                                <TableCell align="center">{row.porcRecurrentes}%</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold", color: "#18234f" }}>
                                  {row.promedioOrdenesRecurrentes}
                                </TableCell>
                              </TableRow>
                            ))
                          )}

                          {/* Fila Total Acumulado */}
                          {datosTabla2.length > 0 && (
                            <TableRow sx={{ backgroundColor: "#f8fafc", borderTop: "2px solid #18234f" }}>
                              <TableCell sx={{ fontWeight: "bold", color: "#18234f", fontSize: "0.95rem" }}>
                                TOTALES / PROMEDIO GENERAL
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                {totalesTabla2.unicaVisita.toLocaleString()}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                {totalesTabla2.recurrentes.toLocaleString()}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#18234f", backgroundColor: "#e2e8f0" }}>
                                {totalesTabla2.atendidos.toLocaleString()}
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                {totalesTabla2.porcUnica}%
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                {totalesTabla2.porcRecurrentes}%
                              </TableCell>
                              <TableCell align="center" sx={{ fontWeight: "bold", color: "#18234f", fontSize: "1rem" }}>
                                {totalesTabla2.promedioOrdenesRecurrentes}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  </Paper>
                </Stack>
              )}
            </Paper>
          </Stack>
        </Box>

        {/* Botón flotante lateral derecho para exportar PDF */}
        <Box
          sx={{
            position: "fixed",
            right: "3vw",
            top: "35vh",
            display: "flex",
            flexDirection: "column",
            gap: 2,
            zIndex: 1000,
          }}
        >
          <Tooltip
            title="Exportar Reporte a PDF"
            arrow
            placement="left"
            slotProps={{
              popper: {
                sx: {
                  [`&.${tooltipClasses.popper}[data-popper-placement*="left"] .${tooltipClasses.tooltip}`]: {
                    marginRight: "0px",
                  },
                },
              },
              tooltip: {
                sx: {
                  fontSize: "0.85vw",
                  padding: "0.5vw 0.8vw",
                  borderRadius: "8px",
                  backgroundColor: "rgba(24, 35, 79, 0.9)",
                  fontWeight: "bold",
                  color: "#ffffff",
                },
              },
              arrow: {
                sx: {
                  color: "rgba(24, 35, 79, 0.9)",
                },
              },
            }}
          >
            <IconButton
              onClick={handleExportarPDF}
              sx={{
                backgroundColor: "#ffffff",
                color: "#d32f2f",
                boxShadow: 3,
                border: "1px solid #e0e0e0",
                "&:hover": { backgroundColor: "#d1d1d1" },
                width: "3.5vw",
                height: "3.5vw",
                minWidth: "48px",
                minHeight: "48px",
              }}
            >
              <Icon path={mdiFilePdfBox} size={1.5} color="#d32f2f" />
            </IconButton>
          </Tooltip>
        </Box>
      </Content>
    </Layout>
  );
};

export default EstadisticasPacientes;
